import React, { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

type FormData = {
  name: string;
  email: string;
  phone: string;
  propertyAddress: string;
  situation: string;
  message: string;
};

const SellHouseFastPage = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError(null);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d67c555bd17lru29kd90',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString(),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Network response was not ok: ${response.status} – ${errorText}`
        );
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyAddress: '',
        situation: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Sell House Fast El Paso TX | Get Cash Offer in 24 Hours | TREX RE LLC"
        description="Need to sell your house fast in El Paso, TX? Get a fair cash offer within 24 hours. No repairs, no commissions, no hassle. Close in as little as 7 days. Local cash home buyers serving all El Paso neighborhoods."
        keywords="sell house fast El Paso, cash home buyers El Paso TX, sell my house quickly, we buy houses El Paso, fast home sale Texas, sell house as-is El Paso"
        canonical="/elpaso-tx/sell-house-fast"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'How to Sell Your House Fast in El Paso, TX: Complete Guide',
          description:
            'Comprehensive guide to selling your house quickly in El Paso, Texas. Learn proven strategies, avoid common mistakes, and get a fair cash offer.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/0d9a92ee2a3718cd7b95dcf1d7fdb3a0.jpg"
            alt="Sell House Fast in El Paso TX"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
                Fast Home Sale Guide
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                How to Sell Your House Fast in{' '}
                <span className="text-teal-400">El Paso, TX</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Discover proven strategies to sell your El Paso home quickly without sacrificing value. Whether you need to relocate, avoid foreclosure, or simply want a hassle-free sale, this comprehensive guide covers everything you need to know about fast home sales in the Sun City.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+1-915-206-5814"
                  className="inline-flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (915) 206-5814
                </a>
                <a
                  href="/get-cash-offer"
                  className="inline-flex items-center px-6 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Cash Offer
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Get Your Free Cash Offer
              </h3>
              <p className="text-gray-600 mb-6">
                Receive a fair offer within 24 hours - no obligation
              </p>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-teal-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    Thank You!
                  </h4>
                  <p className="text-gray-600">
                    We&apos;ll contact you within 24 hours with your cash offer.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="true"
                  id="sell-fast-hero-form"
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    name="propertyAddress"
                    placeholder="Property Address"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
                  </button>
                  {submitError && (
                    <p className="text-red-600 text-sm mt-2">{submitError}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Life happens fast, and sometimes you need to sell your house even faster. Whether you are facing a job relocation, going through a divorce, dealing with an{' '}
              <a
                href="/inherited-homes"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                inherited property you cannot maintain
              </a>
              , or simply ready to move on to your next chapter, the
              traditional home-selling process with its months of showings,
              negotiations, and uncertainty may not fit your timeline. The good
              news is that El Paso homeowners have multiple options for selling
              quickly while still getting a fair price for their property.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              The El Paso real estate market presents unique opportunities for
              fast home sales. With a steady influx of military families from
              Fort Bliss, a growing healthcare and manufacturing sector, and
              affordable housing prices compared to other Texas metros, there is
              consistent demand for homes across all price ranges and
              conditions. Understanding how to leverage these market dynamics
              can help you sell your house fast without leaving money on the
              table.
            </p>

            <p className="text-gray-700 leading-relaxed">
              This comprehensive guide will walk you through every option
              available for selling your El Paso home quickly. From working with{' '}
              <a
                href="/elpaso-tx/cash-home-buyers-el-paso"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                local cash home buyers who can close in days
              </a>{' '}
              to strategic pricing techniques that attract multiple offers, you
              will learn exactly what it takes to achieve a fast sale in
              today&apos;s market. We will also cover common mistakes that slow
              down sales and how to avoid them, ensuring your home-selling
              experience is as smooth and profitable as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Why Sell Fast Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why El Paso Homeowners Need to{' '}
                <span className="text-teal-600">Sell Fast</span>
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Every homeowner&apos;s situation is unique, but certain
                  circumstances make a fast sale not just desirable but
                  essential. Understanding your motivation helps you choose the
                  right selling strategy and set realistic expectations for
                  the process. Here are the most common reasons El Paso
                  homeowners seek quick sales:
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-briefcase-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        Job Relocation
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Corporate transfers and new job opportunities often
                        come with tight timelines. Carrying two mortgages or
                        paying rent while your house sits on the market can
                        drain your finances quickly.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-star-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        Military PCS Orders
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Fort Bliss families know that PCS orders wait for no
                        one. When you receive orders, you typically have weeks,
                        not months, to relocate. A{' '}
                        <a
                          href="/elpaso-tx/selling-home-during-pcs"
                          className="text-teal-600 hover:text-teal-700 font-semibold"
                        >
                          fast sale during PCS
                        </a>{' '}
                        prevents the stress of managing a property from across
                        the country.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-bank-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        Financial Hardship
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Job loss, medical bills, or other financial challenges
                        can make mortgage payments impossible. Selling quickly
                        can help you{' '}
                        <a
                          href="/avoid-foreclosure"
                          className="text-teal-600 hover:text-teal-700 font-semibold"
                        >
                          avoid foreclosure and protect your credit
                        </a>
                        .
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-heart-pulse-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Life Changes</h4>
                      <p className="text-gray-600 text-sm">
                        Divorce, death of a spouse, or health issues requiring a
                        move to assisted living all create urgency. These
                        emotional situations benefit from a streamlined selling
                        process.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-home-gear-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        Property Condition Issues
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Homes needing major repairs, dealing with code violations,
                        or properties that have been neglected can be difficult
                        to sell traditionally. Cash buyers often purchase these
                        homes as‑is.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-group-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">
                        Tenant Problems
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Dealing with problem tenants, non-paying renters, or
                        properties with lease complications can be exhausting.{' '}
                        <a
                          href="/elpaso-tx/we-buy-houses-with-tenants"
                          className="text-teal-600 hover:text-teal-700 font-semibold"
                        >
                          Selling with tenants in place
                        </a>{' '}
                        to a cash buyer eliminates these headaches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=stressed%20homeowner%20looking%20at%20calendar%20and%20financial%20documents%20on%20kitchen%20table%20with%20moving%20boxes%20in%20background%20showing%20urgency%20of%20fast%20home%20sale%20warm%20natural%20lighting%20professional%20photography&width=700&height=600&seq=sell-fast-why-102&orientation=portrait"
                alt="Homeowner Needing to Sell Fast"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Options for Selling Fast Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Options for Selling{' '}
              <span className="text-teal-600">Fast in El Paso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each selling method has its own timeline, costs, and trade‑offs.
              Understanding your options helps you make the best decision for
              your situation.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white border-2 border-teal-500 rounded-xl p-8 relative">
              <div className="absolute -top-4 left-6">
                <span className="px-4 py-2 bg-teal-600 text-white text-sm font-bold rounded-full">
                  Fastest Option
                </span>
              </div>
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6 mt-4">
                <i className="ri-money-dollar-circle-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Sell to a Cash Buyer
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-teal-600"></i>
                  <span className="text-gray-700">
                    <strong>Timeline:</strong> 7-14 days
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-percent-line text-teal-600"></i>
                  <span className="text-gray-700">
                    <strong>Commissions:</strong> $0
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-hammer-line text-teal-600"></i>
                  <span className="text-gray-700">
                    <strong>Repairs:</strong> None required
                  </span>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                Local cash buyers like TREX RE LLC purchase homes directly,
                eliminating the need for listings, showings, and buyer financing
                contingencies. You receive a fair cash offer and can close on
                your timeline.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>No cleaning or staging required</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>No open houses or showings</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Guaranteed closing date</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span>Works for any property condition</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <div className="w-16 h-16 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-auction-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Price Aggressively with Agent
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-amber-600"></i>
                  <span className="text-gray-700">
                    <strong>Timeline:</strong> 30-60 days
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-percent-line text-amber-600"></i>
                  <span className="text-gray-700">
                    <strong>Commissions:</strong> 5-6%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-hammer-line text-amber-600"></i>
                  <span className="text-gray-700">
                    <strong>Repairs:</strong> Usually needed
                  </span>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                Pricing your home 5‑10% below market value can generate multiple
                offers quickly. This strategy works best for move‑in ready homes
                in desirable neighborhoods where buyers are actively competing.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-600 mt-0.5"></i>
                  <span>May attract multiple offers</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-amber-600 mt-0.5"></i>
                  <span>Professional marketing exposure</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-0.5"></i>
                  <span>Still requires showings and negotiations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-0.5"></i>
                  <span>Buyer financing can fall through</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-3xl text-gray-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Sell FSBO (By Owner)
              </h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-gray-600"></i>
                  <span className="text-gray-700">
                    <strong>Timeline:</strong> 60-120+ days
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-percent-line text-gray-600"></i>
                  <span className="text-gray-700">
                    <strong>Commissions:</strong> 0-3%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-hammer-line text-gray-600"></i>
                  <span className="text-gray-700">
                    <strong>Repairs:</strong> Usually needed
                  </span>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                Selling without an agent saves commission but requires
                significant time and effort. This option is generally not
                recommended if speed is your priority, as FSBO homes
                typically take longer to sell.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-gray-600 mt-0.5"></i>
                  <span>Save on listing agent commission</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-gray-600 mt-0.5"></i>
                  <span>Full control over the process</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-0.5"></i>
                  <span>Limited buyer exposure</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-0.5"></i>
                  <span>Requires significant time investment</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-xl p-6">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-teal-600 mr-4 mt-1"></i>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Which Option Is Right for You?
                </h4>
                <p className="text-gray-700">
                  If speed is your top priority and you need certainty,
                  selling to a cash buyer is typically the best choice. If you
                  have 30‑60 days and a move‑in ready home, aggressive pricing
                  with an agent can work well. FSBO is best suited for sellers
                  with flexible timelines who are willing to invest significant
                  time in the process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cash Buyer Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Selling to a Cash Buyer{' '}
              <span className="text-teal-600">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The fastest way to sell your El Paso home is working with a
              reputable local cash buyer. Here is exactly what to expect from
              the process.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Contact Us with Your Property Details
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Call us or fill out our simple online form with basic
                    information about your property. We will ask about the
                    address, condition, and your timeline. This initial
                    conversation takes just a few minutes and there is
                    absolutely no obligation.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Receive Your Cash Offer Within 24 Hours
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We analyze comparable sales, current market conditions,
                    and your property&apos;s specifics to prepare a fair
                    cash offer. Unlike traditional buyers, we do not need
                    to wait for bank approval or sell another home first.
                    Your offer is guaranteed.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Schedule a Quick Property Visit
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    If you like our initial offer, we will schedule a brief
                    walkthrough at your convenience. This is not an
                    inspection designed to find problems and reduce our
                    offer. We simply verify the property details and confirm
                    our offer amount.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Choose Your Closing Date
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    You decide when to close. Need to sell in 7 days? We can
                    make it happen. Need a few weeks to find your next home?
                    No problem. We work around your schedule, not the other
                    way around.
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Close and Get Paid
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    We handle all the paperwork and work with a reputable
                    local title company to ensure a smooth closing. You
                    receive your funds via wire transfer or cashier&apos;s
                    check on closing day. No waiting, no uncertainty.
                  </p>
                </div>
              </div>
            </div>

            <div className="sticky top-8">
              <img
                src="https://readdy.ai/api/search-image?query=happy%20homeowner%20shaking%20hands%20with%20real%20estate%20investor%20after%20successful%20cash%20home%20sale%20in%20front%20of%20southwestern%20style%20house%20el%20paso%20texas%20warm%20golden%20hour%20lighting%20showing%20satisfaction%20and%20relief%20professional%20photography&width=700&height=600&seq=sell-fast-process-103&orientation=portrait"
                alt="Successful Cash Home Sale"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cash Sale vs. Traditional Sale:{' '}
              <span className="text-teal-600">Side-by-Side Comparison</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See exactly how selling to a cash buyer compares to listing with a
              real estate agent.
            </p>
          </div>

          <div className="overflow-x-auto mb-12">
            <table className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                    Factor
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-teal-600">
                    Cash Buyer Sale
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-600">
                    Traditional Agent Sale
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Time to Close
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    7-14 days
                  </td>
                  <td className="px-6 py-4 text-gray-700">60-90+ days</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Agent Commissions
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">$0</td>
                  <td className="px-6 py-4 text-gray-700">
                    5-6% of sale price
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Repairs Required
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    None - sell as-is
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    Usually $5,000-$20,000+
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Cleaning & Staging
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    Not needed
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    Required ($1,000-$5,000)
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Showings & Open Houses
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    None
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    Multiple per week
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Buyer Financing Risk
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    Zero - guaranteed cash
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    High - 20% of deals fall through
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Closing Costs
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    We cover most costs
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    Seller pays 2-4%
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Holding Costs
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    Minimal (1-2 weeks)
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    2-3+ months of mortgage, utilities, taxes
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Contingencies
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    None
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    Inspection, appraisal, financing
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">
                    Stress Level
                  </td>
                  <td className="px-6 py-4 text-teal-600 font-semibold">
                    Low - simple process
                  </td>
                  <td className="px-6 py-4 text-gray-700">
                    High - constant uncertainty
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-teal-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">
                $15,000+
              </div>
              <p className="text-gray-700 font-semibold">Average Savings</p>
              <p className="text-gray-600 text-sm mt-2">
                When you factor in commissions, repairs, staging, and holding
                costs
              </p>
            </div>
            <div className="bg-teal-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">
                60+ Days
              </div>
              <p className="text-gray-700 font-semibold">Time Saved</p>
              <p className="text-gray-600 text-sm mt-2">
                Close in days instead of months with a cash buyer
              </p>
            </div>
            <div className="bg-teal-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
              <p className="text-gray-700 font-semibold">Closing Certainty</p>
              <p className="text-gray-600 text-sm mt-2">
                No financing contingencies or last‑minute surprises
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=frustrated%20homeowner%20looking%20at%20house%20for%20sale%20sign%20that%20has%20been%20on%20market%20too%20long%20overgrown%20yard%20showing%20stale%20listing%20southwestern%20neighborhood%20el%20paso%20texas%20warm%20afternoon%20lighting%20professional%20real%20estate%20photography&width=700&height=500&seq=sell-fast-mistakes-104&orientation=landscape"
                alt="Common Home Selling Mistakes"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                7 Mistakes That{' '}
                <span className="text-teal-600">Slow Down Home Sales</span>
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Overpricing Your Home
                    </h4>
                    <p className="text-gray-600 text-sm">
                      The biggest mistake sellers make is pricing too high.
                      Overpriced homes sit on the market, become stale
                      listings, and eventually sell for less than if they had
                      been priced correctly from the start.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Neglecting Curb Appeal
                    </h4>
                    <p className="text-gray-600 text-sm">
                      First impressions matter. An overgrown yard, peeling
                      paint, or cluttered entrance can turn buyers away before
                      they even step inside. Simple improvements make a big
                      difference.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Hiding Property Issues
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Trying to hide problems always backfires. Issues will be
                      discovered during inspection, killing deals and wasting
                      time. Disclose everything upfront or{' '}
                      <a
                        href="/elpaso-tx/sell-house-without-agent"
                        className="text-teal-600 hover:text-teal-700 font-semibold"
                      >
                        sell as-is to a cash buyer
                      </a>
                      .
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Being Inflexible with Showings
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Limiting showing times to narrow windows makes it hard for
                      buyers to see your home. The more accessible your
                      property, the faster it will sell.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Poor Quality Photos
                    </h4>
                    <p className="text-gray-600 text-sm">
                      In today&apos;s digital market, photos are everything.
                      Dark, blurry, or poorly composed photos will get your
                      listing skipped. Professional photography is essential.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Ignoring Feedback
                    </h4>
                    <p className="text-gray-600 text-sm">
                      If multiple buyers mention the same concern, listen. Maybe
                      your price is too high, or a specific room needs
                      attention. Adapt based on market feedback.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">
                      Choosing the Wrong Agent
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Not all agents are created equal. An inexperienced or
                      part-time agent can cost you time and money. Choose
                      someone with a proven track record in fast sales.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* El Paso Market Insights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              El Paso Market Insights for{' '}
              <span className="text-teal-600">Fast Sales</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding local market dynamics helps you position your home
              for a quick sale at the best possible price.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=el%20paso%20texas%20real%20estate%20market%20graph%20showing%20home%20sales%20trends%20with%20franklin%20mountains%20skyline%20in%20background%20modern%20professional%20business%20photography%20showing%20growth%20and%20opportunity&width=700&height=500&seq=sell-fast-market-105&orientation=landscape"
                alt="El Paso Real Estate Market"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Current Market Conditions
              </h3>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">
                      Average Days on Market
                    </span>
                    <span className="text-2xl font-bold text-teal-600">
                      45-60
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Traditional sales in El Paso typically take 45-60 days.
                    Cash buyers can close in 7-14 days.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">
                      Median Home Price
                    </span>
                    <span className="text-2xl font-bold text-teal-600">
                      $245K
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    El Paso remains one of the most affordable major metros in
                    Texas, attracting buyers from pricier markets.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">
                      Inventory Levels
                    </span>
                    <span className="text-2xl font-bold text-teal-600">
                      Balanced
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    El Paso has a balanced market with steady demand from
                    military families, healthcare workers, and first-time
                    buyers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-teal-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-star-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Fort Bliss Impact
              </h4>
              <p className="text-gray-600">
                With over 35,000 military personnel, Fort Bliss creates
                consistent demand for housing. Military families often need to
                buy or sell quickly due to PCS orders.
              </p>
            </div>

            <div className="bg-teal-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Growing Economy
              </h4>
              <p className="text-gray-600">
                El Paso&apos;s healthcare, manufacturing, and logistics sectors
                are expanding, bringing new residents who need housing. This
                creates opportunities for fast sales.
              </p>
            </div>

            <div className="bg-teal-50 rounded-xl p-8">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-heart-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Affordable Market
              </h4>
              <p className="text-gray-600">
                Compared to Austin, Dallas, and Houston, El Paso offers
                exceptional value. This affordability attracts buyers from
                across Texas and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions About{' '}
              <span className="text-teal-600">Selling Fast</span>
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How fast can I really sell my house in El Paso?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                With a cash buyer like TREX RE LLC, you can close in as little
                as 7 days. Traditional sales with an agent typically take 60-90
                days from listing to closing. The timeline depends on your
                chosen method, property condition, and how quickly you need to
                move.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Will I get less money selling to a cash buyer?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Not necessarily. While cash offers may be slightly lower than
                retail market value, you save thousands on commissions (5-6%),
                repairs, staging, holding costs, and closing costs. When you
                factor in all expenses and the guaranteed closing, many sellers
                net more money selling to a cash buyer.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do I need to make repairs before selling?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                If you sell to a cash buyer, no repairs are needed. We buy
                houses in any condition - from pristine to severely damaged.
                Traditional sales usually require repairs based on inspection
                findings, which can cost $5,000-$20,000 or more.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What if my house has title issues or liens?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Cash buyers often purchase properties with title complications,
                tax liens, or other issues that would prevent a traditional
                sale. We work with title companies to resolve these problems as
                part of the closing process.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell if I&apos;m behind on mortgage payments?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Yes. If you are facing foreclosure or behind on payments, a
                quick sale can help you avoid foreclosure and protect your
                credit. We can work with your lender to facilitate a smooth
                transaction and help you move forward.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What areas of El Paso do you buy houses in?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                We buy houses throughout El Paso County, including all
                neighborhoods from East El Paso to the Westside, from the Lower
                Valley to Northeast El Paso. We also purchase properties in
                surrounding areas like Socorro, Horizon City, and Canutillo.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Is there any obligation when I request a cash offer?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Absolutely not. Requesting a cash offer is completely free with
                no obligation whatsoever. We will provide a fair offer based on
                your property&apos;s condition and market value, and you can
                take all the time you need to decide if it&apos;s right for
                you.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How do I know if selling fast is the right choice for me?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Selling fast makes sense if you are facing foreclosure, need to
                relocate quickly, have inherited a property you do not want,
                have a house that needs major repairs, or simply want to avoid
                the hassle of traditional selling. If any of these situations
                apply to you, a cash sale is worth considering.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Middle CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-xl p-8 md:p-12 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Ready to Sell Your El Paso Home Fast?
              </h2>
              <p className="text-xl text-gray-600">
                Get your free, no-obligation cash offer today
              </p>
            </div>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h4>
                <p className="text-gray-600">
                  We&apos;ll contact you within 24 hours with your cash offer.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form="true"
                id="sell-fast-middle-form"
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    name="propertyAddress"
                    placeholder="Property Address"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <select
                  name="situation"
                  value={formData.situation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">
                    What&apos;s Your Situation? (Optional)
                  </option>
                  <option value="relocating">Relocating for Work</option>
                  <option value="military-pcs">Military PCS</option>
                  <option value="inherited">Inherited Property</option>
                  <option value="divorce">Divorce</option>
                  <option value="foreclosure">Facing Foreclosure</option>
                  <option value="repairs-needed">Home Needs Repairs</option>
                  <option value="tenant-issues">Tenant Issues</option>
                  <option value="downsizing">Downsizing</option>
                  <option value="other">Other</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Tell us more about your property and timeline (optional)..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                ></textarea>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Get My Free Cash Offer'}
                </button>
                {submitError && (
                  <p className="text-red-600 text-sm mt-2">{submitError}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose TREX RE LLC Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose{' '}
              <span className="text-teal-600">TREX RE LLC</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are local El Paso investors who understand the market and care
              about helping homeowners find solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Local Experts
              </h4>
              <p className="text-gray-600 text-sm">
                We live and work in El Paso. We know the neighborhoods, market
                values, and what makes each area unique.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Fair Offers
              </h4>
              <p className="text-gray-600 text-sm">
                We provide honest, transparent offers based on real market data.
                No lowball tactics or bait-and-switch.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-speed-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                Fast Closings
              </h4>
              <p className="text-gray-600 text-sm">
                We can close in as little as 7 days, or on your preferred
                timeline. You choose the closing date that works for you.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                No Hassles
              </h4>
              <p className="text-gray-600 text-sm">
                No repairs, no showings, no commissions, no fees. We handle
                everything and make the process simple.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related Resources
            </h2>
            <p className="text-xl text-gray-600">
              Learn more about selling your El Paso home
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a
              href="/elpaso-tx/cash-home-buyers-el-paso"
              className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20investor%20reviewing%20property%20documents%20with%20homeowner%20at%20modern%20office%20desk%20el%20paso%20texas%20showing%20trust%20and%20professionalism%20warm%20lighting&width=400&height=300&seq=sell-fast-related-106&orientation=landscape"
                  alt="Cash Home Buyers El Paso"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Cash Home Buyers in El Paso
                </h4>
                <p className="text-gray-600 text-sm">
                  Learn how cash buyers work and what to expect when selling
                  your home for cash in El Paso.
                </p>
              </div>
            </a>

            <a
              href="/avoid-foreclosure"
              className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=relieved%20homeowner%20signing%20documents%20with%20real%20estate%20professional%20avoiding%20foreclosure%20showing%20hope%20and%20new%20beginning%20bright%20office%20setting%20professional%20photography&width=400&height=300&seq=sell-fast-related-107&orientation=landscape"
                  alt="Avoid Foreclosure"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  How to Avoid Foreclosure
                </h4>
                <p className="text-gray-600 text-sm">
                  Discover your options for avoiding foreclosure and protecting
                  your credit through a quick sale.
                </p>
              </div>
            </a>

            <a
              href="/elpaso-tx/selling-home-during-pcs"
              className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="relative h-48">
                <img
                  src="https://readdy.ai/api/search-image?query=military%20service%20member%20in%20uniform%20with%20family%20standing%20in%20front%20of%20house%20with%20sold%20sign%20fort%20bliss%20el%20paso%20texas%20patriotic%20professional%20real%20estate%20photography&width=400&height=300&seq=sell-fast-related-108&orientation=landscape"
                  alt="Selling During PCS"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Selling Your Home During PCS
                </h4>
                <p className="text-gray-600 text-sm">
                  Military families: Learn how to sell your home quickly when
                  you receive PCS orders from Fort Bliss.
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SellHouseFastPage;
